import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import userService from '../../../services/userService';
import {
  cpfDigitsOnly,
  maskCellphoneBr,
  maskCpf,
  maskPersonName,
  phoneDigitsOnly,
} from '../../../utils/masks';

const PersonalInfo = () => {
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [savedFullName, setSavedFullName] = useState('');
  const [cpf, setCpf] = useState('');
  const [savedCpf, setSavedCpf] = useState('');
  const [cellphone, setCellphone] = useState('');
  const [savedCellphone, setSavedCellphone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const [userResult, configResponse] = await Promise.all([
          userService.getUserInfo(),
          userService.loadUserConfigurations(),
        ]);

        if (cancelled) return;

        if (userResult?.success && userResult.data?.email) {
          setEmail(userResult.data.email);
        }

        const config = configResponse?.configuration ?? {};
        const name = String(config.full_name ?? '').trim();
        const cpfDigits = cpfDigitsOnly(config.cpf ?? '');
        const phoneDigits = phoneDigitsOnly(config.cellphone ?? '');

        setFullName(name);
        setSavedFullName(name);
        setCpf(maskCpf(cpfDigits));
        setSavedCpf(cpfDigits);
        setCellphone(maskCellphoneBr(phoneDigits));
        setSavedCellphone(phoneDigits);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading personal info:', err);
          setError('Não foi possível carregar as informações pessoais.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const currentFullName = String(fullName || '').trim();
  const currentCpf = cpfDigitsOnly(cpf);
  const currentCellphone = phoneDigitsOnly(cellphone);
  const hasPendingChanges =
    currentFullName !== savedFullName ||
    currentCpf !== savedCpf ||
    currentCellphone !== savedCellphone;

  const handleSave = async () => {
    if (!hasPendingChanges) return;

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const formData = new FormData();
      formData.append('full_name', currentFullName);
      formData.append('cpf', currentCpf);
      formData.append('cellphone', currentCellphone);

      const result = await userService.saveUserConfigurations(formData);

      if (!result?.success) {
        throw new Error(
          result?.message || 'Não foi possível salvar as informações pessoais.'
        );
      }

      const refreshed = await userService.loadUserConfigurations();
      const config = refreshed?.configuration ?? {};
      const name = String(config.full_name ?? currentFullName).trim();
      const cpfDigits = cpfDigitsOnly(config.cpf ?? currentCpf);
      const phoneDigits = phoneDigitsOnly(config.cellphone ?? currentCellphone);

      setFullName(name);
      setSavedFullName(name);
      setCpf(maskCpf(cpfDigits));
      setSavedCpf(cpfDigits);
      setCellphone(maskCellphoneBr(phoneDigits));
      setSavedCellphone(phoneDigits);
      setSuccessMessage('Informações pessoais salvas com sucesso.');
    } catch (err) {
      setError(
        err?.message || 'Não foi possível salvar as informações pessoais.'
      );
    } finally {
      setSaving(false);
    }
  };

  const clearFeedback = () => {
    setSuccessMessage('');
    setError('');
  };

  return (
    <section className="bg-card rounded-lg shadow-elevation-md p-4 md:p-6 lg:p-8 transition-smooth">
      <button
        type="button"
        className="flex w-full items-center gap-3 text-left"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg shrink-0">
          <Icon name="User" size={20} color="var(--color-primary)" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Informações Pessoais
          </h2>
          <p className="text-sm text-muted-foreground font-body mt-1">
            Dados usados para preencher sua assinatura nos documentos
          </p>
        </div>
        <Icon
          name={expanded ? 'ChevronDown' : 'ChevronRight'}
          size={20}
          className="shrink-0 text-muted-foreground"
        />
      </button>

      {expanded ? (
        <div className="mt-4 md:mt-6">
          {loading ? (
            <p className="text-sm text-muted-foreground font-body">Carregando...</p>
          ) : (
            <div className="space-y-4 md:space-y-5 max-w-md">
              <Input
                type="text"
                label="Nome Completo"
                value={fullName}
                onChange={(e) => {
                  setFullName(maskPersonName(e.target.value));
                  clearFeedback();
                }}
                placeholder="Nome completo"
                autoComplete="name"
                disabled={saving}
              />

              <Input
                type="text"
                label="CPF"
                value={cpf}
                onChange={(e) => {
                  setCpf(maskCpf(e.target.value));
                  clearFeedback();
                }}
                placeholder="000.000.000-00"
                inputMode="numeric"
                autoComplete="off"
                disabled={saving}
              />

              <Input
                type="email"
                label="Email"
                value={email}
                readOnly
                disabled
                autoComplete="email"
              />

              <Input
                type="tel"
                label="Celular"
                value={cellphone}
                onChange={(e) => {
                  setCellphone(maskCellphoneBr(e.target.value));
                  clearFeedback();
                }}
                placeholder="(00) 0 0000-0000"
                inputMode="numeric"
                autoComplete="tel"
                disabled={saving}
              />

              {error ? (
                <p className="text-sm text-destructive font-body">{error}</p>
              ) : null}

              {successMessage ? (
                <p className="text-sm text-success font-body">{successMessage}</p>
              ) : null}

              <div className="flex justify-end pt-1">
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !hasPendingChanges}
                  loading={saving}
                >
                  Salvar
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
};

export default PersonalInfo;
